import { ApiError, apiFetch } from "@pontimed/api-client";
import { queryOptions, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { sessionStatusAtom } from "../atoms";
import type { UserProfile } from "../types";

export const userProfileQueryKey = ["api", "/api/account/user/profile"] as const;

/**
 * Shared definition — use `userProfileQueryOptions` for TanStack Router
 * `loader` prefetch on authenticated routes:
 *   `loader: ({ context }) => context.queryClient.ensureQueryData(userProfileQueryOptions)`
 * Both the loader and `useUserProfile()` share the same `queryKey` so the
 * cache is hit on component mount instead of triggering a duplicate fetch.
 */
export const userProfileQueryOptions = queryOptions({
  queryKey: userProfileQueryKey,
  queryFn: async (): Promise<UserProfile | null> => {
    try {
      return await apiFetch.get<UserProfile>("/api/account/user/profile");
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 5 * 60_000,
  gcTime: 30 * 60_000,
});

/**
 * Rich user profile (email, name, photoUrl, etc.) from
 * GET /api/account/user/profile. Auto-skipped when unauthenticated.
 */
export function useUserProfile(): UseQueryResult<UserProfile | null> {
  const status = useAtomValue(sessionStatusAtom);
  return useQuery({
    ...userProfileQueryOptions,
    enabled: status === "authenticated",
  });
}
